import { makeAutoObservable, runInAction } from 'mobx';
import createStoreContext from '../createStoreContext';
import { getAllUsers } from 'src/services/user';
import type {
  ApiResponse,
  PaginatedResponse,
} from 'src/types/commonResponseTypes';
import type { UserApiResponse } from 'src/types/user';
import {
  USERS_LIMIT_DEFAULT,
  USERS_PAGE_DEFAULT,
  USER_LOOKUP_CACHE_TTL_MS,
} from 'src/constants/user';
import { handleError } from 'src/utils/commonErrorUtils';
import { ZERO } from 'src/constants/utils';

interface FetchOrganizationUsersLookupParams {
  page?: number;
  limit?: number;
  search?: string;
  organizationId?: string | null;
}

export class UserStore {
  activeOrganizationId: string | null = null;
  organizationUsersLookup: UserApiResponse[] = [];
  organizationUsersLookupTotalCount = ZERO;
  isOrganizationUsersLookupLoading = false;
  organizationUsersLookupError: string | null = null;
  organizationUserIdentitiesById: Record<string, UserApiResponse> = {};
  private lookupRequestRevision = 0;
  organizationUsersLookupCacheByQuery: Record<
    string,
    { items: UserApiResponse[]; totalCount: number; lastFetchedAt: number }
  > = {};

  constructor() {
    makeAutoObservable(this);
  }

  private isCacheFresh(lastFetchedAt: number, ttlMs: number): boolean {
    return Date.now() - lastFetchedAt < ttlMs;
  }

  private getCacheKey(
    search?: string,
    organizationId?: string | null,
    page = USERS_PAGE_DEFAULT,
    limit = USERS_LIMIT_DEFAULT,
  ): string {
    const trimmed = search?.trim() ?? '';
    const orgKey = organizationId ?? 'unknown-org';

    return JSON.stringify([orgKey, trimmed.toLowerCase(), page, limit]);
  }

  private ensureOrganizationContext(organizationId?: string | null) {
    if (organizationId === this.activeOrganizationId) {
      return;
    }

    this.activeOrganizationId = organizationId ?? null;
    this.lookupRequestRevision += 1;
    this.organizationUserIdentitiesById = {};
    this.organizationUsersLookupError = null;
    this.organizationUsersLookup = [];
    this.organizationUsersLookupTotalCount = ZERO;
    this.organizationUsersLookupCacheByQuery = {};
  }

  fetchOrganizationUsersLookup = async (
    params: FetchOrganizationUsersLookupParams = {},
    options: { force?: boolean } = {},
  ): Promise<ApiResponse<PaginatedResponse<UserApiResponse[]>>> => {
    const organizationId =
      params.organizationId === undefined
        ? this.activeOrganizationId
        : params.organizationId;

    this.ensureOrganizationContext(organizationId);
    // A cached query also owns the picker: an older network request must not
    // replace it after a user clears search or switches organizations.
    const requestRevision = ++this.lookupRequestRevision;

    this.organizationUsersLookupError = null;
    const page = params.page ?? USERS_PAGE_DEFAULT;
    const limit = params.limit ?? USERS_LIMIT_DEFAULT;
    const search = params.search?.trim() || undefined;
    const cacheKey = this.getCacheKey(search, organizationId, page, limit);
    const cached = this.organizationUsersLookupCacheByQuery[cacheKey];

    if (
      cached &&
      !options.force &&
      this.isCacheFresh(cached.lastFetchedAt, USER_LOOKUP_CACHE_TTL_MS)
    ) {
      runInAction(() => {
        this.organizationUsersLookup = cached.items;
        this.organizationUsersLookupTotalCount = cached.totalCount;
        this.isOrganizationUsersLookupLoading = false;
      });

      return {
        success: true,
        content: {
          data: cached.items,
          totalCount: cached.totalCount,
        },
      };
    }

    runInAction(() => {
      this.isOrganizationUsersLookupLoading = true;
    });

    let response: ApiResponse<PaginatedResponse<UserApiResponse[]>>;

    try {
      response = await getAllUsers<UserApiResponse[]>(page, limit, search);
    } catch (error) {
      response = await handleError(error);
    }

    runInAction(() => {
      if (
        requestRevision !== this.lookupRequestRevision ||
        organizationId !== this.activeOrganizationId
      )
        return;
      this.isOrganizationUsersLookupLoading = false;

      if (response.success && response.content) {
        this.organizationUsersLookup = response.content.data;
        this.organizationUsersLookupTotalCount = response.content.totalCount;
        this.organizationUsersLookupCacheByQuery[cacheKey] = {
          items: response.content.data,
          totalCount: response.content.totalCount,
          lastFetchedAt: Date.now(),
        };
        // Keep selected identities when the next search result is another
        // page. Never fabricate membership or silently drop an existing owner.
        this.organizationUserIdentitiesById = {
          ...this.organizationUserIdentitiesById,
          ...Object.fromEntries(
            response.content.data.map((user) => [user._id, user]),
          ),
        };
      } else {
        this.organizationUsersLookupError = response.message ?? '';
        this.organizationUsersLookup = cached?.items ?? [];
        this.organizationUsersLookupTotalCount = cached?.totalCount ?? ZERO;
      }
    });

    return response;
  };
}

export const [UserStoreProvider, useUserStore] = createStoreContext(UserStore);
